import {
  selectIsLocalScreenShared,
  selectIsSomeoneScreenSharing,
  useCustomEvent,
  useHMSActions,
  useHMSStore,
} from "@100mslive/react-sdk";
import { t } from "@/i18n-js/instance";
import { useElectronAPI } from "@/react/hooks/utils/useElectronAPI";
import { notifyBugsnag } from "@circle-react/helpers/bugsnagHelpers";
import { useToast } from "@circle-react-uikit/ToastV2";
import type { ProviderScreenSharing } from "../types";
import type { ModerationMessage } from "./constants";
import { MODERATOR_MESSAGES, MODERATOR_TYPE } from "./constants";

const OS_ERROR = "SystemDeniedPermission";

export const useHMSScreenSharing = (
  canOverride = false,
): ProviderScreenSharing => {
  const { error } = useToast();
  const hmsActions = useHMSActions();
  const isScreenSharingEnabled = useHMSStore(selectIsLocalScreenShared);
  const isSomeoneScreenSharing = useHMSStore(selectIsSomeoneScreenSharing);
  const { isElectron } = useElectronAPI();

  const onModeratorEvent = (message: ModerationMessage) => {
    if (
      message === MODERATOR_MESSAGES.STOP_SCREENSHARE &&
      isScreenSharingEnabled
    ) {
      void hmsActions.setScreenShareEnabled(false);
    }
  };

  const { sendEvent } = useCustomEvent({
    type: MODERATOR_TYPE,
    onEvent: onModeratorEvent,
  });

  const toggleScreenSharing = async () => {
    if (!isScreenSharingEnabled && isSomeoneScreenSharing && !canOverride) {
      return;
    }
    try {
      if (!isScreenSharingEnabled && isSomeoneScreenSharing) {
        // Override remote screenshare
        sendEvent(MODERATOR_MESSAGES.STOP_SCREENSHARE);
      }
      await hmsActions.setScreenShareEnabled(!isScreenSharingEnabled, {
        systemAudio: "include",
      });
    } catch (err: unknown) {
      if (isElectron) {
        error(t("live_streams.room.electron_screen_sharing_browser_error"));
        return;
      }
      if (err instanceof Error && err.name === OS_ERROR) {
        error(t("live_streams.room.screen_sharing_os_error"));
        return;
      }
      error(t("live_streams.room.screen_sharing_browser_error"));

      console.error(err);
      if (err instanceof Error) {
        notifyBugsnag(err);
      }
    }
  };

  return {
    isScreenSharingEnabled,
    isSomeoneScreenSharing,
    toggleScreenSharing,
  };
};
